import LogoJson from "../../assets/lottie/Logo.json";
import { LottieOptions, useLottie } from "lottie-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";

const registry = {
  logo: LogoJson,
};

export type LottieKeys = keyof typeof registry;

export type LottieAnimsProps = {
  name: LottieKeys;
  needsTrigger?: boolean;
  isstopped?: string;
} & Omit<LottieOptions, "animationData">;

export const LottieAnim: FC<LottieAnimsProps> = ({
  name,
  isstopped,
  needsTrigger,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  const options: LottieOptions = {
    ...rest,
    animationData: registry[name],
    loop: isstopped ? false : true,
    autoplay: false,
  };

  const { View, stop, setSpeed, play } = useLottie(options);

  useEffect(() => {
    setSpeed(0.5);
  }, [setSpeed]);

  const onMouseHover = useCallback(() => {
    if (needsTrigger) {
      play();
    }
  }, [needsTrigger, play]);

  const onMouseLeave = useCallback(() => {
    if (needsTrigger) {
      stop();
    }
  }, [needsTrigger, stop]);

  useEffect(() => {
    const node = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayed) {
          setTimeout(play, 500);
          setHasPlayed(true);
        } else if (!entry.isIntersecting) {
          setTimeout(stop, 500);
        }
      },
      { threshold: 1 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [play, stop, hasPlayed]);

  return (
    <div
      ref={ref}
      className="lottie-container"
      onMouseEnter={onMouseHover}
      onMouseLeave={onMouseLeave}
    >
      {View}
    </div>
  );
};
